import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLegendState as useState } from "@/hooks/useLegendState";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { User, AlertCircle, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Agent, AgentPersona, AgentMemoryPolicy } from "@/types/aiToolkit";
import { ICON_OPTIONS } from "@/lib/agentBuildConstants";
import { validateAgentBuild, defaultOutputRequirements } from "@/lib/validateAgentBuild";
import { agentFoldersService, type AgentFolder } from "@/services/agentFoldersService";
import { sourcesService } from "@/services/sourcesService";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { AgentStrengthMeter } from "@/components/agents/AgentStrengthMeter";
import { AgentKnowledgeAttach } from "@/components/agents/AgentKnowledgeAttach";
import { AgentAdvancedSection } from "@/components/agents/AgentAdvancedSection";

export const defaultPersona: AgentPersona = {
  role: "",
  expertise: [],
  communicationStyle: "professional",
  systemPrompt: "",
};

export const defaultMemoryPolicy: AgentMemoryPolicy = {
  type: "session",
  includeContext: true,
};

export const defaultAgentPartial: Partial<Agent> = {
  name: "",
  description: "",
  tier: "agent",
  nodeType: "specialist",
  category: "general",
  icon: ICON_OPTIONS[0],
  color: "bg-purple-500",
  tags: [],
  access: "free",
  persona: defaultPersona,
  capabilityModules: [],
  memoryPolicy: defaultMemoryPolicy,
  outputRequirements: defaultOutputRequirements(),
  linkedSourceIds: [],
  trainingDocumentIds: [],
  lens: null,
  expertFocus: null,
  folderId: null,
};

export interface AgentBuildFormProps {
  value: Partial<Agent>;
  onChange: (agent: Partial<Agent>) => void;
  defaultFolderId?: string | null;
  showFolderPicker?: boolean;
  scrollHeight?: string;
  fillContainer?: boolean;
}

export function AgentBuildForm({
  value,
  onChange,
  defaultFolderId,
  showFolderPicker = true,
  scrollHeight = "400px",
  fillContainer = false,
}: AgentBuildFormProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const formData = value;

  const setFormData = (updater: Partial<Agent> | ((prev: Partial<Agent>) => Partial<Agent>)) => {
    if (typeof updater === "function") {
      onChange(updater(formData));
    } else {
      onChange({ ...formData, ...updater });
    }
  };

  const [newExpertise, setNewExpertise] = useState("");
  const [requireTrainingContext, setRequireTrainingContext] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (defaultFolderId && !formData.folderId && formData.createdBy !== "user") {
      setFormData((prev) => ({ ...prev, folderId: defaultFolderId }));
    }
  }, [defaultFolderId, formData.folderId, formData.createdBy]);

  const { data: folders = [] } = useQuery<AgentFolder[]>({
    queryKey: ["/api/agent-folders-flat"],
    queryFn: () => agentFoldersService.getFolders(),
    enabled: showFolderPicker,
  });

  const { data: sources = [] } = useQuery({
    queryKey: ["/api/sources", "agent-build"],
    queryFn: () => sourcesService.getSources(),
  });

  const { data: kbItems = [] } = useQuery({
    queryKey: ["/api/knowledge-base", "agent-build"],
    queryFn: () => knowledgeBaseService.getKnowledgeBaseItems(),
  });

  const validation = useMemo(
    () => validateAgentBuild(formData, requireTrainingContext),
    [formData, requireTrainingContext]
  );

  const folderOptions = useMemo(() => {
    const opts: { id: string; label: string }[] = [{ id: "__none__", label: "No folder" }];
    for (const f of folders) {
      opts.push({ id: f.id, label: f.name });
    }
    return opts;
  }, [folders]);

  const addExpertise = () => {
    if (newExpertise.trim() && formData.persona) {
      setFormData((prev) => ({
        ...prev,
        persona: {
          ...prev.persona!,
          expertise: [...(prev.persona?.expertise || []), newExpertise.trim()],
        },
      }));
      setNewExpertise("");
    }
  };

  const removeExpertise = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      persona: {
        ...prev.persona!,
        expertise: prev.persona?.expertise?.filter((_, i) => i !== index) || [],
      },
    }));
  };

  const scrollToSection = (sectionId: string) => {
    if (sectionId === "agent-section-advanced") {
      setAdvancedOpen(true);
    }
    requestAnimationFrame(() => {
      const el = scrollRef.current?.querySelector(`#${sectionId}`);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div className="w-full min-w-0 h-full min-h-0 overflow-hidden flex flex-col">
      {(!validation.valid || validation.warnings.length > 0) && (
        <div className="mb-3 space-y-1 shrink-0">
          {validation.errors.map((err) => (
            <p key={err} className="text-sm text-destructive flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {err}
            </p>
          ))}
          {validation.warnings.map((w) => (
            <p key={w} className="text-sm text-amber-600 dark:text-amber-500 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {w}
            </p>
          ))}
        </div>
      )}

      {fillContainer && (
        <p className="text-xs text-muted-foreground mb-2 shrink-0">
          Scroll for instructions, knowledge, and advanced settings.
        </p>
      )}

      <div
        ref={scrollRef}
        className={cn(
          "min-h-0 w-full overflow-y-auto pr-1",
          fillContainer ? "flex-1" : undefined
        )}
        style={fillContainer ? undefined : { maxHeight: scrollHeight }}
      >
        <div className="min-w-0 w-full max-w-full pr-2 space-y-8 pb-4">
          <div className="sticky top-0 z-10 bg-background py-2 -mx-1 px-1">
            <AgentStrengthMeter value={formData} onNudgeClick={scrollToSection} />
          </div>

          {/* ① Identity */}
          <section id="agent-section-identity" className="space-y-4 scroll-mt-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <Label className="text-base font-medium">Identity</Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-name">Agent Name</Label>
              <Input
                id="agent-name"
                value={formData.name || ""}
                onChange={(e) => setFormData({ name: e.target.value })}
                placeholder="e.g., Research Assistant"
                data-testid="input-agent-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="agent-description">Purpose</Label>
              <Textarea
                id="agent-description"
                value={formData.description || ""}
                onChange={(e) => setFormData({ description: e.target.value })}
                placeholder="One-line description of what this agent can do..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-2">
                {ICON_OPTIONS.map((emoji) => (
                  <Button
                    key={emoji}
                    type="button"
                    variant={formData.icon === emoji ? "default" : "outline"}
                    size="sm"
                    className="h-9 w-9 p-0 text-lg"
                    onClick={() => setFormData({ icon: emoji })}
                  >
                    {emoji}
                  </Button>
                ))}
              </div>
            </div>
          </section>

          {/* ② Instructions */}
          <section id="agent-section-instructions" className="space-y-4 scroll-mt-4">
            <Label className="text-base font-medium">Instructions</Label>
            <p className="text-sm text-muted-foreground -mt-2">
              Tell your agent what to do and how to behave — this shapes its system prompt.
            </p>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Input
                id="role"
                value={formData.persona?.role || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    persona: { ...prev.persona!, role: e.target.value },
                  }))
                }
                placeholder="e.g., Senior Research Analyst"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="systemPrompt">System Prompt</Label>
              <Textarea
                id="systemPrompt"
                value={formData.persona?.systemPrompt || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    persona: { ...prev.persona!, systemPrompt: e.target.value },
                  }))
                }
                placeholder="You are a helpful assistant that..."
                rows={8}
                className="font-mono text-sm"
                data-testid="textarea-agent-prompt"
              />
              <p className="text-xs text-muted-foreground text-right">
                {(formData.persona?.systemPrompt || "").length} characters
              </p>
            </div>
            <div className="space-y-2">
              <Label>Expertise Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={newExpertise}
                  onChange={(e) => setNewExpertise(e.target.value)}
                  placeholder="Add expertise area..."
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addExpertise())}
                />
                <Button type="button" size="sm" onClick={addExpertise}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.persona?.expertise?.map((exp, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => removeExpertise(index)}
                  >
                    {exp}
                    <X className="h-3 w-3 ml-1" />
                  </Badge>
                ))}
              </div>
            </div>
          </section>

          {/* ③ Knowledge */}
          <AgentKnowledgeAttach
            value={formData}
            onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
            sources={sources}
            kbItems={kbItems}
            requireTrainingContext={requireTrainingContext}
            onRequireTrainingContextChange={setRequireTrainingContext}
          />

          {/* Advanced */}
          <AgentAdvancedSection
            value={formData}
            onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
            showFolderPicker={showFolderPicker}
            folderOptions={folderOptions}
            open={advancedOpen}
            onOpenChange={setAdvancedOpen}
          />
        </div>
      </div>
    </div>
  );
}

export { validateAgentBuild };
